const supportedTypes = new Set(['boolean', 'int', 'long', 'float', 'double', 'string', 'enum'])
const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key)
const object = value => value && typeof value === 'object' && !Array.isArray(value)
const present = value => value !== undefined && value !== null && value !== ''
const scalar = value => ['string', 'boolean'].includes(typeof value) || (typeof value === 'number' && Number.isFinite(value))
const integerPattern = /^[+-]?\d+$/
const numberPattern = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i

function invalid (text) { throw new Error(text) }

function flag (value) {
  // 对齐 reactor-ql CastUtils.castBoolean：Boolean原值、Number.intValue()==1、六个忽略大小写字符串。
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return Number.isFinite(value) && Math.trunc(value) === 1
  return typeof value === 'string' && ['true', 'y', 'ok', 'yes', '1', 'on'].includes(value.toLowerCase())
}

function integer (input) {
  if (typeof input === 'number' && !Number.isSafeInteger(input)) invalid('整数必须使用精确的十进制值')
  if (!['number', 'string'].includes(typeof input)) invalid('请输入有效整数')
  const text = String(input).trim()
  if (!integerPattern.test(text)) invalid('请输入有效的十进制整数')
  const negative = text[0] === '-'
  const digits = text.replace(/^[+-]/, '').replace(/^0+(?=\d)/, '')
  return negative && digits !== '0' ? '-' + digits : digits
}

function compareInteger (left, right) {
  const a = integer(left), b = integer(right)
  const negativeA = a[0] === '-', negativeB = b[0] === '-'
  if (negativeA !== negativeB) return negativeA ? -1 : 1
  const x = negativeA ? a.slice(1) : a, y = negativeB ? b.slice(1) : b
  const order = x.length === y.length ? (x === y ? 0 : x < y ? -1 : 1) : x.length < y.length ? -1 : 1
  return negativeA ? -order : order
}

function number (input) {
  if (!['number', 'string'].includes(typeof input)) invalid('请输入有效数值')
  const text = String(input).trim()
  if (!numberPattern.test(text)) invalid('请输入有效的十进制数值')
  const value = Number(text)
  if (!Number.isFinite(value)) invalid('数值必须是有限数值')
  // 避免非常小的非零值在 Number 转换时悄悄变成 0。
  if (value === 0 && /[1-9]/.test(text.split(/e/i)[0])) invalid('数值精度超出可支持范围')
  return value === 0 ? 0 : value
}

function decimalPlaces (input) {
  const parts = String(input).trim().toLowerCase().split('e')
  const coefficient = parts[0].replace(/^[+-]/, '')
  const fraction = (coefficient.split('.')[1] || '').length
  const digits = coefficient.replace('.', '')
  if (!/[1-9]/.test(digits)) return 0
  const trailing = (digits.match(/0+$/) || [''])[0].length
  return Math.max(0, fraction - Number(parts[1] || 0) - trailing)
}

function booleanValues (spec) {
  const trueValue = own(spec, 'trueValue') ? spec.trueValue : true
  const falseValue = own(spec, 'falseValue') ? spec.falseValue : false
  if (!scalar(trueValue) || !scalar(falseValue) || Object.is(trueValue, falseValue)) invalid('布尔属性的开关值配置无效')
  return { trueValue, falseValue }
}

function enumElements (spec) {
  if (!Array.isArray(spec.elements) || !spec.elements.length) invalid('枚举属性没有有效候选值')
  return spec.elements.map(entry => {
    if (!object(entry) || !own(entry, 'value') || !scalar(entry.value)) invalid('枚举候选值格式无效')
    return { text: present(entry.text) ? String(entry.text) : String(entry.value), value: entry.value }
  })
}

function constraints (spec) {
  const result = {}
  if (['int', 'long', 'float', 'double'].includes(spec.type)) {
    for (const key of ['min', 'max']) {
      if (present(spec[key])) result[key] = spec.type === 'long' ? integer(spec[key]) : number(spec[key])
    }
    if (own(result, 'min') && own(result, 'max') && (spec.type === 'long'
      ? compareInteger(result.min, result.max) > 0 : result.min > result.max)) invalid('物模型最小值不能大于最大值')
    if (present(spec.scale)) {
      const scale = number(spec.scale)
      if (!Number.isInteger(scale) || scale < 0 || scale > 1000) invalid('物模型小数精度无效')
      result.scale = scale
    }
  }
  if (spec.type === 'string' && present(spec.maxLength)) {
    const maxLength = number(spec.maxLength)
    if (!Number.isSafeInteger(maxLength) || maxLength < 0) invalid('物模型字符长度无效')
    result.maxLength = maxLength
  }
  return result
}

/** 仅在点击提交时规范化目标值；不将空值、false 或 0 隐式转换为其他目标。 */
export function normalizeControlValue (spec, input) {
  if (!object(spec) || !supportedTypes.has(spec.type)) invalid('当前不支持此属性类型')
  if (spec.writable !== true) invalid('该属性不可写')
  const limits = constraints(spec)
  if (spec.type === 'boolean') {
    const values = booleanValues(spec)
    if (input === true) return values.trueValue
    if (input === false) return values.falseValue
    if (Object.is(input, values.trueValue)) return values.trueValue
    if (Object.is(input, values.falseValue)) return values.falseValue
    return invalid('请选择有效的开关值')
  }
  if (spec.type === 'enum') {
    const entry = enumElements(spec).find(entry => Object.is(entry.value, input))
    if (!entry) invalid('请选择物模型声明的枚举值')
    return entry.value
  }
  if (spec.type === 'string') {
    if (typeof input !== 'string') invalid('请输入字符串，不接受对象或其他类型')
    if (own(limits, 'maxLength') && input.length > limits.maxLength) invalid('字符长度超出物模型约束')
    return input
  }
  if (spec.type === 'long') {
    const value = integer(input)
    if (compareInteger(value, '-9223372036854775808') < 0 || compareInteger(value, '9223372036854775807') > 0) invalid('整数超出 Long 的 64 位范围')
    if ((own(limits, 'min') && compareInteger(value, limits.min) < 0) || (own(limits, 'max') && compareInteger(value, limits.max) > 0)) invalid('数值超出物模型范围')
    return value
  }
  const value = spec.type === 'int' ? Number(integer(input)) : number(input)
  if (spec.type === 'int' && (!Number.isInteger(value) || value < -2147483648 || value > 2147483647)) invalid('整数超出 Int 的 32 位范围')
  if (spec.type === 'float' && (!Number.isFinite(Math.fround(value)) || (value !== 0 && Math.fround(value) === 0))) invalid('数值超出 Float 的范围')
  if ((own(limits, 'min') && value < limits.min) || (own(limits, 'max') && value > limits.max)) invalid('数值超出物模型范围')
  if (own(limits, 'scale') && decimalPlaces(input) > limits.scale) invalid('小数位数超出物模型精度')
  return value === 0 ? 0 : value
}

/** 显式 type 与平台 accessMode / readOnly 取交集，不能把只读上报属性当作可写。 */
function writable (property) {
  const expands = object(property.expands) ? property.expands : {}
  const source = expands.source === undefined ? property.source : expands.source
  if (['rule', 'manual', 'virtual'].includes(source) || flag(expands.virtual) || flag(property.virtual)) return false
  if (own(expands, 'type')) {
    const modes = expands.type
    if (!Array.isArray(modes) || !modes.length || modes.some(mode => !['read', 'write', 'report'].includes(mode)) || !modes.includes('write')) return false
  }
  if (own(expands, 'accessMode')) return String(expands.accessMode).includes('w')
  if (own(expands, 'readOnly')) return !flag(expands.readOnly)
  if (own(property, 'readOnly')) return !flag(property.readOnly)
  return true
}

/** 编辑器候选来自完整物模型；这里只给出可配置候选，不代表服务器已授予写权限。 */
export function editorWritableProperties (metadata) {
  let model = metadata
  if (typeof model === 'string') {
    if (!model.trim()) return []
    try { model = JSON.parse(model) } catch (error) { invalid('设备物模型不是有效的 JSON') }
  }
  if (model === undefined || model === null) return []
  if (!object(model) || !Array.isArray(model.properties)) invalid('设备物模型属性格式无效')
  const ids = new Set()
  return model.properties.filter(property => object(property) && object(property.valueType) &&
    typeof property.id === 'string' && property.id.trim() && !['__proto__', 'constructor', 'prototype'].includes(property.id) &&
    supportedTypes.has(property.valueType.type) && writable(property)).map(property => {
    if (ids.has(property.id)) invalid('设备物模型属性标识重复')
    ids.add(property.id)
    const type = property.valueType
    const spec = { id: property.id, name: property.name || property.id, type: type.type, writable: true,
      unit: typeof type.unit === 'string' ? type.unit : object(type.unit) ? type.unit.symbol || type.unit.name || '' : '' }
    const expands = object(type.expands) ? type.expands : {}
    for (const key of ['min', 'max', 'scale', 'maxLength']) {
      const value = own(type, key) ? type[key] : expands[key]
      // 原物模型JSON中的Long边界可能已被浏览器解析失真，不能猜回原值。
      // 编辑器只绑定属性；正式提交使用服务器cap返回的精确字符串边界。
      if (type.type === 'long' && ['min', 'max'].includes(key) && typeof value === 'number' && !Number.isSafeInteger(value)) continue
      if (own(type, key) || own(expands, key)) spec[key] = value
    }
    Object.assign(spec, constraints(spec))
    if (type.type === 'boolean') {
      const values = { ...expands, ...type }
      Object.assign(spec, booleanValues(values), { trueText: displayText(values.trueText, 128) || '开启', falseText: displayText(values.falseText, 128) || '关闭' })
    }
    if (type.type === 'enum') spec.elements = enumElements({ ...expands, ...type })
    return spec
  })
}

function displayText (value, max) {
  if (typeof value !== 'string' || !value.trim() || value.length > max) return ''
  const hasControl = Array.from(value).some(character => {
    const code = character.charCodeAt(0)
    return code <= 31 || (code >= 127 && code <= 159)
  })
  return hasControl ? '' : value
}

/** 编辑／预览只保存最小展示提示；不保存物模型、目标值或任何可写授权结论。 */
export function propertyDisplayHint (property) {
  if (!object(property) || !supportedTypes.has(property.type) || !displayText(property.id, 128) ||
      ['__proto__', 'constructor', 'prototype'].includes(property.id)) return null
  const hint = { id: property.id, name: displayText(property.name, 128) || property.id, type: property.type,
    unit: displayText(property.unit, 32) }
  const limits = constraints(property)
  if (['float', 'double'].includes(property.type) && own(limits, 'scale')) hint.scale = limits.scale
  if (property.type === 'string' && own(limits, 'maxLength')) hint.maxLength = limits.maxLength
  if (property.type === 'boolean') {
    hint.trueText = displayText(property.trueText, 128) || '开启'
    hint.falseText = displayText(property.falseText, 128) || '关闭'
  }
  if (property.type === 'enum') {
    if (!Array.isArray(property.elements) || property.elements.length > 200) invalid('枚举展示候选数量无效')
    hint.elements = enumElements(property).map(entry => ({ value: entry.value, text: displayText(entry.text, 128) || String(entry.value) }))
  }
  return hint
}

export function controlDeviceId (option = {}, context = {}) {
  if (!object(option) || !['fixed', 'page'].includes(option.deviceSource)) invalid('请选择有效的设备来源')
  const id = option.deviceSource === 'page' ? context && context.deviceId : option.deviceId
  if (typeof id !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(id)) invalid(option.deviceSource === 'page'
    ? '当前页面没有有效设备 ID，请从设备列表进入' : '请选择一个有效的固定设备')
  return id
}
